// src/pages/RoomPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Client } from '@stomp/stompjs';
import { useMediaQuery } from 'react-responsive';
import sessionManager from '../utils/sessionManager';
import useVoiceRoom from "../hooks/useVoiceRoom";
import RoomHeader from '../components/room/RoomHeader';
import RoomVideoSection from '../components/room/RoomVideoSection';
import RoomChatSection from '../components/room/RoomChatSection';
import RoomVoicePanel from '../components/room/RoomVoicePanel';
import MobileMenu from '../components/room/MobileMenu';
import UserList from '../components/UserList';

const API_BASE = "https://api.syncplay.live";
const WS_URL = "wss://api.syncplay.live/ws";

function RoomPage() {
  const { roomId: paramRoomId } = useParams();
  const navigate = useNavigate();

  const [copied, setCopied] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [uiRoomId, setUiRoomId] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [fileName, setFileName] = useState('');
  const [users, setUsers] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  const [showVoicePanel, setShowVoicePanel] = useState(false);
  
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [showChat, setShowChat] = useState(true);
  const [chatWidth, setChatWidth] = useState(230);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenChatOpen, setFullscreenChatOpen] = useState(true);
  
  const [ready, setReady] = useState(false);
  
  const videoRef = useRef(null);
  const roomContentRef = useRef(null);
  const chatContainerRef = useRef(null);
  const ignoreEvent = useRef(false);
  const senderName = useRef('');

  const clientRef = useRef(null);
  const subscriptionRef = useRef(null);
  const roomIdRef = useRef("");
  const showChatRef = useRef(true);
  const isChatMinimizedRef = useRef(false);

  const voice = useVoiceRoom(showVoicePanel ? uiRoomId : null, senderName.current);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    if (uiRoomId && senderName.current) {
      window.dispatchEvent(new CustomEvent('voiceUsernameChanged', {
        detail: { username: senderName.current }
      }));
    }
  }, [uiRoomId, senderName.current]);

  const getOrCreateClientId = () => {
    let cid = sessionManager.getSession()?.sessionId;
    if (cid) return cid;
    const stored = localStorage.getItem('syncplay_clientId');
    if (stored) return stored;
    const newId = Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('syncplay_clientId', newId);
    return newId;
  };

  const clientId = useRef(getOrCreateClientId());

  useEffect(() => {
    const saved = sessionManager.getUsername();
    if (saved && saved.trim().length >= 2) {
      senderName.current = saved;
      setReady(true);
    }
    const handleUsernameSet = () => {
      const username = sessionManager.getUsername();
      if (username && username.trim().length >= 2) {
        senderName.current = username;
        setReady(true);
      }
    };
    window.addEventListener('usernameSet', handleUsernameSet);
    return () => window.removeEventListener('usernameSet', handleUsernameSet);
  }, []);

  const applyVideoEvent = useCallback((data) => {
    const video = videoRef.current;
    if (!video) return;
    ignoreEvent.current = true;
    if (typeof data.currentTime === "number") video.currentTime = data.currentTime;
    if (data.action === "PLAY") video.play().catch();
    if (data.action === "PAUSE") video.pause();
    if (data.action === "SEEK") video.currentTime = data.currentTime;
    setTimeout(() => { ignoreEvent.current = false; }, 300);
  }, []);

  const send = useCallback((action) => {
    const video = videoRef.current;
    if (!video || ignoreEvent.current || !roomIdRef.current) return;
    if (!clientRef.current?.connected) {
    }
    clientRef.current.publish({
      destination: `/app/rooms/${roomIdRef.current}/video-sync`,
      body: JSON.stringify({ senderId: clientId.current, action, currentTime: video.currentTime })
    });
  }, []);

  const sendChatMessage = useCallback(() => {
    const text = chatInput.trim();
    if (!text || !roomIdRef.current || !clientRef.current?.connected) return;
    clientRef.current.publish({
      destination: `/app/rooms/${roomIdRef.current}/chat`,
      body: JSON.stringify({ 
        senderId: clientId.current, 
        senderName: senderName.current, 
        text,
        replyTo: replyTo ? { senderName: replyTo.senderName, text: replyTo.text } : null
      })
    });
    setChatInput('');
    setReplyTo(null);
  }, [chatInput, replyTo]);

  const handleChatEvent = useCallback((data) => {
    setMessages(prev => {
      if (prev.some(m => m.senderId === data.senderId && m.text === data.text && m.sentAt === data.sentAt)) return prev;
      if (data.senderId !== clientId.current && (!showChatRef.current || isChatMinimizedRef.current)) {
        setUnreadCount(p => p + 1);
      }
      return [...prev, { 
        senderId: data.senderId, 
        senderName: data.senderName || 'Guest', 
        text: data.text, 
        sentAt: data.sentAt, 
        isMine: data.senderId === clientId.current,
        replyTo: data.replyTo || null
      }];
    });
  }, []);

  const fetchRoomUsers = useCallback(async () => {
    const rid = roomIdRef.current;
    if (!rid) return;
    try {
      const res = await fetch(`${API_BASE}/rooms/${rid}/users`);
      const data = await res.json();
      if (data.success && data.data) {
        const seen = new Set();
        const unique = data.data.filter(u => u?.username && !seen.has(u.username.toLowerCase()) && seen.add(u.username.toLowerCase()));
        setUsers(unique);
      }
    } catch (error) {
    }
  }, []);

  const subscribeRoom = useCallback(() => {
    if (!clientRef.current?.connected) return;
    if (subscriptionRef.current) { subscriptionRef.current.unsubscribe(); subscriptionRef.current = null; }
    subscriptionRef.current = clientRef.current.subscribe(`/topic/rooms/${roomIdRef.current}/events`, (msg) => {
      try {
        const event = JSON.parse(msg.body);
        if (!event.data) return;
        if (event.type === 'REQUEST_TIME') {
          const video = videoRef.current;
          if (video && video.src && clientRef.current?.connected) {
            clientRef.current.publish({
              destination: `/app/rooms/${roomIdRef.current}/sync-time`,
              body: JSON.stringify({ currentTime: video.currentTime, targetId: event.data.senderId })
            });
          }
          return;
        }
        if (event.type === 'SYNC_TIME' && event.data.targetId === clientId.current) {
          const video = videoRef.current;
          if (video && typeof event.data.currentTime === 'number') {
            ignoreEvent.current = true;
            video.currentTime = event.data.currentTime;
            setTimeout(() => { ignoreEvent.current = false; }, 500);
          }
          return;
        }
        if (event.type === 'USER_LIST_UPDATE') {
          const seen = new Set();
          const unique = (Array.isArray(event.data) ? event.data : []).filter(u => u?.username && !seen.has(u.username.toLowerCase()) && seen.add(u.username.toLowerCase()));
          setUsers(unique); return;
        }
        if (event.type === 'VIDEO_SYNC' && event.data.senderId !== clientId.current) { applyVideoEvent(event.data); return; }
        if (event.type === 'CHAT') { handleChatEvent(event.data); return; }
        if (!event.type && event.data.senderId !== clientId.current) {
          if (event.data.action) applyVideoEvent(event.data);
          else if (event.data.text) handleChatEvent(event.data);
        }
      } catch (error) {
      }
    });
    setTimeout(() => fetchRoomUsers(), 1500);
  }, [applyVideoEvent, handleChatEvent, fetchRoomUsers]);

  const connectWS = useCallback(() => {
    if (clientRef.current?.connected) { subscribeRoom(); return; }
    clientRef.current = new Client({
      brokerURL: WS_URL, reconnectDelay: 3000, heartbeatIncoming: 15000, heartbeatOutgoing: 15000,
      connectHeaders: { 'client-id': clientId.current, 'username': senderName.current || 'anonymous', 'room-id': roomIdRef.current },
      onConnect: () => { setIsConnected(true); subscribeRoom(); },
      onDisconnect: () => setIsConnected(false),
      onWebSocketError: () => setIsConnected(false),
      onStompError: () => setIsConnected(false),
    });
    clientRef.current.activate();
  }, [subscribeRoom]);

  useEffect(() => {
    if (!ready) return;
    const initRoom = async () => {
      const id = paramRoomId.trim().toUpperCase();
      try {
        const res = await fetch(`${API_BASE}/rooms/${id}`);
        const data = await res.json();
        if (!data.success) { alert("Room not found"); navigate('/'); return; }
        roomIdRef.current = id;
        setUiRoomId(id);
        showChatRef.current = true;
        isChatMinimizedRef.current = false;
        connectWS();
      } catch (err) { navigate('/'); }
    };
    initRoom();
    return () => {
      subscriptionRef.current?.unsubscribe();
      clientRef.current?.deactivate();
    };
  }, [paramRoomId, ready, navigate, connectWS]);

  const handleFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setFileName(file.name);
    const video = videoRef.current;
    if (video) {
      video.onplay = null; video.onpause = null; video.onseeked = null;
      if (video.src?.startsWith('blob:')) URL.revokeObjectURL(video.src);
      video.src = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        if (clientRef.current?.connected && roomIdRef.current) {
          clientRef.current.publish({
            destination: `/app/rooms/${roomIdRef.current}/request-time`,
            body: JSON.stringify({ senderId: clientId.current })
          });
        }
      };
      video.onplay = () => send("PLAY");
      video.onpause = () => send("PAUSE");
      video.onseeked = () => send("SEEK");
    }
  };

  const leaveRoom = () => {
    fetch(`${API_BASE}/rooms/${roomIdRef.current}/leave?clientId=${clientId.current}`).catch(() => {});
    if (isFullscreen) { document.exitFullscreen?.() || document.webkitExitFullscreen?.(); }
    setTimeout(() => {
      subscriptionRef.current?.unsubscribe(); subscriptionRef.current = null;
      clientRef.current?.deactivate({ force: true }); clientRef.current = null;
      roomIdRef.current = "";
      const video = videoRef.current;
      if (video) { video.onplay = video.onpause = video.onseeked = null; if (video.src?.startsWith('blob:')) URL.revokeObjectURL(video.src); video.removeAttribute('src'); }
      navigate('/');
    }, 300);
  };

  const handleFullscreenToggle = useCallback(() => {
    const c = roomContentRef.current; if (!c) return;
    if (!isFullscreen) c.requestFullscreen?.() || c.webkitRequestFullscreen?.();
    else document.exitFullscreen?.() || document.webkitExitFullscreen?.();
  }, [isFullscreen]);

  useEffect(() => {
    const h = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', h); document.addEventListener('webkitfullscreenchange', h);
    return () => { document.removeEventListener('fullscreenchange', h); document.removeEventListener('webkitfullscreenchange', h); };
  }, []);

  useEffect(() => {
    const video = videoRef.current; if (!video) return;
    video.onplay = () => send("PLAY"); video.onpause = () => send("PAUSE"); video.onseeked = () => send("SEEK");
    return () => { video.onplay = video.onpause = video.onseeked = null; };
  }, [send]);

  useEffect(() => { if (chatContainerRef.current) chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight; }, [messages]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault(); const sx = e.clientX, sw = chatWidth;
    const mm = (e) => setChatWidth(Math.min(600, Math.max(200, sw + sx - e.clientX)));
    const mu = () => { document.removeEventListener('mousemove', mm); document.removeEventListener('mouseup', mu); document.body.style.cursor = ''; };
    document.addEventListener('mousemove', mm); document.addEventListener('mouseup', mu); document.body.style.cursor = 'col-resize';
  }, [chatWidth]);

  const toggleChat = () => {
    if (isChatMinimized) { setIsChatMinimized(false); isChatMinimizedRef.current = false; setUnreadCount(0); }
    else { setShowChat(!showChat); showChatRef.current = !showChat; if (!showChat) setUnreadCount(0); }
  };
  
  const toggleMinimize = () => { setIsChatMinimized(!isChatMinimized); isChatMinimizedRef.current = !isChatMinimized; if (isChatMinimized) setUnreadCount(0); };

  const [voiceToggling, setVoiceToggling] = useState(false);

  const toggleVoicePanel = () => {
    if (voiceToggling) return;
    setVoiceToggling(true);
    
    if (showVoicePanel) {
      setShowVoicePanel(false);
      voice.disconnect();
    } else {
      setShowVoicePanel(true);
      voice.connect();
    }
    
    setTimeout(() => setVoiceToggling(false), 1000);
  };

  // تابع باز کردن چت در موبایل
  const handleMobileChatOpen = () => {
    if (isChatMinimized) {
      setIsChatMinimized(false);
      isChatMinimizedRef.current = false;
    }
    setShowChat(true);
    showChatRef.current = true;
    setUnreadCount(0);
  };

  const showMiniVoiceOverlay = isFullscreen && voice.connected;
  

  return (
    <>
      {!isMobile && showUserList && (
        <div className="user-list-panel-right">
          <UserList users={users} isOpen={showUserList} onToggle={() => setShowUserList(false)} />
        </div>
      )}

      {!isMobile && showVoicePanel && (
        <RoomVoicePanel voice={voice} onClose={() => setShowVoicePanel(false)} />
      )}

      {!isFullscreen && (
        isMobile ? (
          <MobileMenu 
            isConnected={isConnected}
            uiRoomId={uiRoomId}
            unreadCount={unreadCount}
            users={users}
            currentUsername={senderName.current}
            onChatClick={handleMobileChatOpen}
            onLeaveRoom={leaveRoom}
          />
        ) : (
          <RoomHeader 
            uiRoomId={uiRoomId}
            isConnected={isConnected}
            showChat={showChat}
            showUserList={showUserList}
            showVoicePanel={showVoicePanel}
            onToggleChat={toggleChat}
            onToggleUsers={() => setShowUserList(!showUserList)}
            onToggleVoice={toggleVoicePanel}
            onLeaveRoom={leaveRoom}
            unreadCount={unreadCount}
            usersCount={users.length}
          />
        )
      )}

      <div ref={roomContentRef} className={`room-content ${isFullscreen?'fullscreen':''} ${isMobile && showChat ? 'mobile-chat-active' : ''}`}>
        <RoomVideoSection 
          fileName={fileName}
          isFullscreen={isFullscreen}
          videoRef={videoRef}
          onFileChange={handleFileChange}
          onFullscreenToggle={handleFullscreenToggle}
        />

{/* ⭐ Mini Voice Overlay - فقط توی Fullscreen */}
      {showMiniVoiceOverlay && (
        <div className="mini-voice-overlay">
          {voice.participants.map((p) => (
            <div 
              key={p.id} 
              className={`mini-voice-user ${p.isSpeaking ? 'speaking' : p.isMuted ? 'muted' : 'idle'}`}
            >
              <span className="mini-dot"></span>
              <span className="mini-name">{p.name}</span>
            </div>
          ))}
        </div>
      )}

        {showChat && (
          <>
            {isMobile && (
              <button 
                className="mobile-chat-close"
                onClick={() => {
                  setShowChat(false);
                  showChatRef.current = false;
                }}
              >
                ✕
              </button>
            )}
            <RoomChatSection 
              messages={messages}
              chatInput={chatInput}
              replyTo={replyTo}
              isMinimized={isChatMinimized}
              isFullscreen={isFullscreen}
              fullscreenChatOpen={fullscreenChatOpen}
              width={isMobile ? (showChat ? '280px' : '0px') : chatWidth}
              onWidthChange={setChatWidth}
              onMinimizeToggle={toggleMinimize}
              onClose={() => {
                setShowChat(false);
                showChatRef.current = false;
              }}
              onInputChange={(e) => setChatInput(e.target.value)}
              onSend={sendChatMessage}
              onReplyCancel={() => setReplyTo(null)}
              onReplyClick={(msg) => setReplyTo({ senderName: msg.senderName, text: msg.text })}
              onFullscreenChatToggle={() => setFullscreenChatOpen(!fullscreenChatOpen)}
              onResizeStart={handleResizeStart}
              isMobile={isMobile}
            />
          </>
        )}
      </div>
    </>
  );
}

export default RoomPage;